import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Drawer, Modal, copyText } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  useServedGovernoratesQuery,
  useShipmentActionMutation,
  useShipmentAutoRefresh,
  useShipmentCityMutation,
  useShipmentDetailQuery,
  type ShipmentAction,
} from '../api';
import { availableActions, type EventSource, type ShipmentDetail } from '../types';

import {
  AttentionBadge,
  SHIPMENT_STATUS_KEY,
  ShipmentStatusBadge,
  lastErrorKey,
} from './ShipmentBadges';

import { formatCatalogPrice } from '@/features/products/format-price';

const SOURCE_KEY: Record<EventSource, MessageKey> = {
  carrier_poll: 'merchant.ship.source.carrier_poll',
  carrier_notification: 'merchant.ship.source.carrier_notification',
  create_response: 'merchant.ship.source.create_response',
  system: 'merchant.ship.source.system',
  operator: 'merchant.ship.source.operator',
};

const DONE_KEY: Record<ShipmentAction, MessageKey> = {
  refresh: 'merchant.ship.done.refresh',
  retry: 'merchant.ship.done.retry',
  cancel: 'merchant.ship.done.cancel',
  resend: 'merchant.ship.done.resend',
};

function when(value: string | null, locale: string): string {
  return value === null ? '—' : new Date(value).toLocaleString(locale === 'ar' ? 'ar-TN' : 'fr-TN');
}

/** A money string as the API wrote it; `—` when absent. Never computed with. */
function amount(value: string | null): string {
  return value === null ? '—' : formatCatalogPrice(Number(value));
}

/**
 * The detail of one shipment: what the carrier said (timeline), what Souk did (call journal: CODES only), the amounts
 * exactly as Jibex wrote them, and the actions its state allows. Every action is re-checked by the API.
 */
export function ShipmentDetailDrawer({
  shipmentId,
  onClose,
}: {
  shipmentId: string | null;
  onClose: () => void;
}): ReactElement {
  const { t, locale } = useI18n();
  const detail = useShipmentDetailQuery(shipmentId);
  const action = useShipmentActionMutation();
  const [message, setMessage] = useState<{ tone: 'good' | 'critical'; text: string } | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Automatic refresh every 60 s while a parcel that can still move is open; the « Actualiser » button stays.
  const auto = useShipmentAutoRefresh(
    shipmentId,
    shipmentId !== null &&
      detail.data !== undefined &&
      availableActions(detail.data.shipment).refresh,
    action.isPending,
  );

  useEffect(() => {
    setMessage(null);
    setConfirmCancel(false);
  }, [shipmentId]);

  function run(kind: ShipmentAction): void {
    if (shipmentId === null) return;
    setMessage(null);
    action.mutate(
      { id: shipmentId, action: kind },
      {
        onSuccess: () => {
          setMessage({ tone: 'good', text: t(DONE_KEY[kind]) });
        },
        onError: (error) => {
          setMessage({ tone: 'critical', text: t(resolveErrorMessageKey(error)) });
        },
      },
    );
  }

  return (
    <Drawer open={shipmentId !== null} title={t('merchant.ship.detail.title')} onClose={onClose}>
      {detail.isPending ? (
        <p className="text-sm text-inkSoft">{t('merchant.common.loadingAria')}</p>
      ) : detail.isError ? (
        <p role="alert" className="text-sm text-critical">
          {t(resolveErrorMessageKey(detail.error))}
        </p>
      ) : (
        <DetailBody
          data={detail.data}
          locale={locale}
          busy={action.isPending}
          autoRefreshing={auto.refreshing}
          lastAutoRefreshAt={auto.lastRefreshedAt}
          message={message}
          onAction={(kind) => {
            if (kind === 'cancel') setConfirmCancel(true);
            else run(kind);
          }}
        />
      )}

      <Modal
        open={confirmCancel}
        title={t('merchant.ship.cancel.title')}
        description={t('merchant.ship.cancel.body')}
        onClose={() => {
          setConfirmCancel(false);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmCancel(false);
              }}
            >
              {t('merchant.ship.cancel.keep')}
            </Button>
            <Button
              variant="danger"
              loading={action.isPending}
              onClick={() => {
                setConfirmCancel(false);
                run('cancel');
              }}
            >
              {t('merchant.ship.cancel.confirm')}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-inkSoft">{t('merchant.ship.cancel.effect')}</p>
      </Modal>
    </Drawer>
  );
}

function DetailBody({
  data,
  locale,
  busy,
  autoRefreshing,
  lastAutoRefreshAt,
  message,
  onAction,
}: {
  data: ShipmentDetail;
  locale: string;
  busy: boolean;
  /** An automatic refresh is running: the button waits for it rather than sending a second, identical call. */
  autoRefreshing: boolean;
  lastAutoRefreshAt: Date | null;
  message: { tone: 'good' | 'critical'; text: string } | null;
  onAction: (action: ShipmentAction) => void;
}): ReactElement {
  const { t } = useI18n();
  const { shipment, order } = data;
  const actions = availableActions(shipment);
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex flex-col gap-5 text-sm">
      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <ShipmentStatusBadge status={shipment.status} />
          {shipment.attentionReason !== null ? (
            <AttentionBadge reason={shipment.attentionReason} />
          ) : null}
        </div>
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
          <dt className="text-inkFaint">{t('merchant.ship.detail.order')}</dt>
          <dd>
            <Link
              to={`/orders?status=${order.status}`}
              className="font-mono text-accent hover:underline"
            >
              #{order.number}
            </Link>{' '}
            · {formatCatalogPrice(Number(order.total))}
          </dd>
          <dt className="text-inkFaint">{t('merchant.ship.detail.recipient')}</dt>
          <dd dir="auto">
            {order.recipientName} —{' '}
            {shipment.cityOverride ?? `${order.governorate}, ${order.delegation}`}
          </dd>
          <dt className="text-inkFaint">{t('merchant.ship.detail.tracking')}</dt>
          <dd className="flex items-center gap-2">
            <span className="font-mono">{shipment.trackingNumber ?? '—'}</span>
            {shipment.trackingNumber !== null ? (
              <button
                type="button"
                className="text-xs text-accent hover:underline"
                onClick={() => {
                  void copyText(shipment.trackingNumber ?? '').then(() => {
                    setCopied(true);
                  });
                }}
              >
                {copied ? t('merchant.ship.detail.copied') : t('merchant.ship.detail.copy')}
              </button>
            ) : null}
          </dd>
          <dt className="text-inkFaint">{t('merchant.ship.detail.reference')}</dt>
          <dd className="font-mono text-xs">{shipment.externalReference ?? '—'}</dd>
          <dt className="text-inkFaint">{t('merchant.ship.detail.carrierStatus')}</dt>
          <dd className="font-mono text-xs">
            {shipment.carrierStatus ?? '—'} · {when(shipment.carrierStatusAt, locale)}
          </dd>
          {shipment.carrierReason !== null ? (
            <>
              <dt className="text-inkFaint">{t('merchant.ship.detail.reason')}</dt>
              <dd dir="auto">{shipment.carrierReason}</dd>
            </>
          ) : null}
          {shipment.cancellationStatus !== null ? (
            <>
              <dt className="text-inkFaint">{t('merchant.ship.detail.cancellation')}</dt>
              <dd dir="auto">
                {shipment.cancellationStatus === 'REQUESTED'
                  ? t('merchant.ship.detail.cancellationRequested')
                  : t('merchant.ship.detail.cancellationRefused')}
                {shipment.cancellationReason !== null ? ` — ${shipment.cancellationReason}` : ''}
              </dd>
            </>
          ) : null}
          {shipment.lastError !== null && shipment.status === 'dispatch_failed' ? (
            <>
              <dt className="text-inkFaint">{t('merchant.ship.detail.lastError')}</dt>
              <dd className="text-critical">
                {(() => {
                  const key = lastErrorKey(shipment.lastError);
                  return key === undefined
                    ? t('merchant.ship.lastError.other', { code: shipment.lastError })
                    : t(key);
                })()}
              </dd>
            </>
          ) : null}
          <dt className="text-inkFaint">{t('merchant.ship.detail.nextPoll')}</dt>
          <dd>{when(shipment.nextPollAt, locale)}</dd>
        </dl>
      </section>

      {message !== null ? (
        <p
          role={message.tone === 'critical' ? 'alert' : 'status'}
          className={message.tone === 'critical' ? 'text-critical' : 'text-good'}
        >
          {message.text}
        </p>
      ) : null}

      <section className="flex flex-wrap gap-2">
        {actions.refresh ? (
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            disabled={autoRefreshing}
            onClick={() => {
              onAction('refresh');
            }}
          >
            {t('merchant.ship.action.refresh')}
          </Button>
        ) : null}
        {actions.retry ? (
          <Button
            size="sm"
            loading={busy}
            onClick={() => {
              onAction('retry');
            }}
          >
            {t('merchant.ship.action.retry')}
          </Button>
        ) : null}
        {actions.cancel ? (
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() => {
              onAction('cancel');
            }}
          >
            {t('merchant.ship.action.cancel')}
          </Button>
        ) : null}
        {actions.resend ? (
          <Button
            size="sm"
            loading={busy}
            onClick={() => {
              onAction('resend');
            }}
          >
            {t('merchant.ship.action.resend')}
          </Button>
        ) : null}
      </section>

      {actions.refresh ? (
        <p className="text-xs text-inkFaint" aria-live="polite">
          {lastAutoRefreshAt === null
            ? t('merchant.ship.auto.note')
            : t('merchant.ship.auto.last', {
                time: lastAutoRefreshAt.toLocaleTimeString(locale === 'ar' ? 'ar-TN' : 'fr-TN'),
              })}
        </p>
      ) : null}

      {actions.editCity ? (
        <CityEditor shipmentId={shipment.id} current={shipment.cityOverride} />
      ) : null}

      <section>
        <h3 className="mb-2 font-semibold text-ink">{t('merchant.ship.detail.timeline')}</h3>
        {data.events.length === 0 ? (
          <p className="text-inkFaint">{t('merchant.ship.detail.noEvents')}</p>
        ) : (
          <ol className="flex flex-col gap-2 border-s border-line ps-3">
            {data.events.map((event) => (
              <li key={event.id}>
                <p className="font-medium text-ink">{t(SHIPMENT_STATUS_KEY[event.status])}</p>
                <p className="text-xs text-inkFaint">
                  {when(event.occurredAt ?? event.observedAt, locale)} ·{' '}
                  {t(SOURCE_KEY[event.source])}
                  {event.location !== null ? ` · ${event.location}` : ''}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section>
        <h3 className="mb-1 font-semibold text-ink">{t('merchant.ship.detail.amounts')}</h3>
        <p className="mb-2 text-xs text-inkFaint">{t('merchant.ship.detail.amountsNotice')}</p>
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
          <dt className="text-inkFaint">{t('merchant.ship.detail.codSent')}</dt>
          <dd>{amount(data.amounts?.codAmountSent ?? null)}</dd>
          <dt className="text-inkFaint">price</dt>
          <dd>{amount(data.amounts?.carrierPrice ?? null)}</dd>
          <dt className="text-inkFaint">amountToCollect</dt>
          <dd>{amount(data.amounts?.carrierAmountToCollect ?? null)}</dd>
        </dl>
      </section>

      <section>
        <h3 className="mb-2 font-semibold text-ink">{t('merchant.ship.detail.journal')}</h3>
        {data.attempts.length === 0 ? (
          <p className="text-inkFaint">—</p>
        ) : (
          <ul className="flex flex-col gap-1 font-mono text-xs text-inkSoft">
            {data.attempts.map((attempt) => (
              <li key={attempt.id}>
                {when(attempt.createdAt, locale)} · {attempt.operation} · {attempt.outcome}
                {attempt.httpStatus !== null ? ` · HTTP ${String(attempt.httpStatus)}` : ''}
                {attempt.carrierErrorCode !== null ? ` · ${attempt.carrierErrorCode}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** The city sent to the carrier (« Gouvernorat, Délégation »), with the governorates Jibex serves as suggestions. */
function CityEditor({
  shipmentId,
  current,
}: {
  shipmentId: string;
  current: string | null;
}): ReactElement {
  const { t } = useI18n();
  const [value, setValue] = useState(current ?? '');
  const [feedback, setFeedback] = useState<{ tone: 'good' | 'critical'; text: string } | null>(
    null,
  );
  const save = useShipmentCityMutation();
  const governorates = useServedGovernoratesQuery(true);
  const listId = `served-governorates-${shipmentId}`;

  function submit(city: string | null): void {
    setFeedback(null);
    save.mutate(
      { id: shipmentId, city },
      {
        onSuccess: () => {
          setFeedback({ tone: 'good', text: t('merchant.ship.city.saved') });
        },
        onError: (error) => {
          setFeedback({ tone: 'critical', text: t(resolveErrorMessageKey(error)) });
        },
      },
    );
  }

  return (
    <section className="flex flex-col gap-2">
      <label htmlFor={`${listId}-input`} className="font-semibold text-ink">
        {t('merchant.ship.city.label')}
      </label>
      <p className="text-xs text-inkFaint">{t('merchant.ship.city.hint')}</p>
      <input
        id={`${listId}-input`}
        className="ecom-input"
        value={value}
        maxLength={120}
        list={listId}
        placeholder="Tunis, Bab Bhar"
        onChange={(event) => {
          setValue(event.currentTarget.value);
        }}
      />
      <datalist id={listId}>
        {(governorates.data ?? []).map((name) => (
          <option key={name} value={`${name}, `} />
        ))}
      </datalist>
      <div className="flex gap-2">
        <Button
          size="sm"
          loading={save.isPending}
          disabled={value.trim().length === 0}
          onClick={() => {
            submit(value.trim());
          }}
        >
          {t('merchant.ship.city.save')}
        </Button>
        {current !== null ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={save.isPending}
            onClick={() => {
              setValue('');
              submit(null);
            }}
          >
            {t('merchant.ship.city.reset')}
          </Button>
        ) : null}
      </div>
      {feedback !== null ? (
        <p
          role={feedback.tone === 'critical' ? 'alert' : 'status'}
          className={feedback.tone === 'critical' ? 'text-critical' : 'text-good'}
        >
          {feedback.text}
        </p>
      ) : null}
    </section>
  );
}
